/*
 * Вход и регистрация: обращения к серверу и решение задачи «не бот».
 */

import type {
	QrLoginCreated,
	QrLoginRequestInfo,
	QrPollState,
	RegistrationDraft,
	RegistrationUpdate,
	ServiceRole,
	VerificationEmailChange,
	MfaChallenge,
	MfaSetup
} from '@andresot/protocol';
import CaptchaWorker from './captcha-worker?worker';
import {
	ApiError,
	request,
	setAccessToken,
	beginAuthorization,
	getAuthorizationGeneration,
	assertAuthorizationGeneration
} from './client.js';
import type { Solved } from './captcha-worker.js';

export interface AccountUser {
	id: string;
	login: string;
	name: string;
	lastName?: string;
	bio?: string;
	avatarUrl?: string;
	avatarColor?: number;
	mailbox?: string;
	isVerified: boolean;
	isPremium: boolean;
	/** Оформление подписчика — приходит, только пока действует Premium. */
	emojiStatus?: string;
	profileAccent?: number;
	serviceRole: ServiceRole;
	emailVerified: boolean;
}

interface AuthResponse {
	user: AccountUser;
	accessToken: string;
	expiresIn: number;
}

export interface RegistrationUpdateResult {
	user: AccountUser;
	registration: RegistrationDraft;
	codeSent: boolean;
	revoked: number;
}

interface Challenge {
	id: string;
	seed: string;
	bits: number;
}

/**
 * Получить задачу и решить её в отдельном потоке.
 *
 * Возвращается вместе со временем подбора — его показывает экран
 * регистрации, чтобы ожидание не выглядело зависанием.
 */
export async function solveCaptcha(): Promise<{ id: string; nonce: string; solved: Solved }> {
	const challenge = await request<Challenge>('/api/captcha');

	const solved = await new Promise<Solved>((resolve, reject) => {
		const worker = new CaptchaWorker();

		// Поток обязательно закрываем: иначе каждая попытка регистрации
		// оставляет за собой висящий поток, занятый перебором.
		const finish = (fn: () => void) => {
			worker.terminate();
			fn();
		};

		worker.onmessage = (event: MessageEvent<Solved>) => finish(() => resolve(event.data));
		worker.onerror = () => finish(() => reject(new Error('Не удалось пройти проверку')));

		worker.postMessage({ seed: challenge.seed, bits: challenge.bits });
	});

	return { id: challenge.id, nonce: solved.nonce, solved };
}

export interface RegisterInput {
	login: string;
	password: string;
	email: string;
	displayName: string;
	/** Логин пригласившего из ссылки /u/<логин>, если человек пришёл по ней. */
	invitedBy?: string;
}

export async function register(input: RegisterInput): Promise<AccountUser> {
	const generation = beginAuthorization();
	const captcha = await solveCaptcha();
	assertAuthorizationGeneration(generation);

	const response = await request<AuthResponse>('/api/auth/register', {
		body: {
			...input,
			captchaId: captcha.id,
			captchaNonce: captcha.nonce
		},
		skipRefresh: true,
		generation
	});

	setAccessToken(response.accessToken, generation);
	return response.user;
}

export async function fetchRegistration(): Promise<RegistrationDraft> {
	const response = await request<{ registration: RegistrationDraft }>('/api/auth/registration');
	return response.registration;
}

export async function updateRegistration(
	input: RegistrationUpdate
): Promise<RegistrationUpdateResult> {
	const generation = getAuthorizationGeneration();
	const response = await request<
		RegistrationUpdateResult & { accessToken?: string; expiresIn?: number }
	>('/api/auth/registration', { method: 'PATCH', body: input });

	if (response.accessToken) setAccessToken(response.accessToken, generation);
	return response;
}

export async function login(loginName: string, password: string): Promise<AccountUser> {
	const generation = beginAuthorization();
	const send = (captcha?: { id: string; nonce: string }) =>
		request<AuthResponse | MfaChallenge>('/api/auth/login', {
			body: {
				login: loginName,
				password,
				...(captcha ? { captchaId: captcha.id, captchaNonce: captcha.nonce } : {})
			},
			skipRefresh: true,
			generation
		});

	let response: AuthResponse | MfaChallenge;
	try {
		response = await send();
	} catch (error) {
		/*
		 * После нескольких неудач подряд сервер просит решить задачу — так он
		 * отличает человека от перебора. Решаем сами и повторяем: показывать
		 * головоломку незачем, счёт идёт на секунду, а человек и так уже
		 * ошибся паролем несколько раз подряд.
		 */
		if (!(error instanceof ApiError) || !error.details?.captchaRequired) throw error;

		const captcha = await solveCaptcha();
		assertAuthorizationGeneration(generation);
		response = await send({ id: captcha.id, nonce: captcha.nonce });
	}
	if ('mfaRequired' in response) throw new MfaRequired(response, generation);

	setAccessToken(response.accessToken, generation);
	return response.user;
}

/**
 * Что сервер разрешает до входа: регистрацию и возврат пароля. Обе
 * возможности упираются в отправку письма с кодом.
 *
 * При недоступном сервере считаем закрытыми обе: показать кнопку и уронить
 * человека на первом же шаге хуже, чем не показать её вовсе.
 */
export class MfaRequired extends Error {
	constructor(
		public challenge: MfaChallenge,
		public generation: number
	) {
		super('Подтвердите вход кодом 2FA');
	}
}
export const sendMfaEnrollmentEmail = (mfa: MfaRequired) =>
	request('/api/auth/mfa/email', {
		body: { challengeId: mfa.challenge.challengeId },
		skipRefresh: true,
		generation: mfa.generation
	});
export async function setupMfaEnrollment(mfa: MfaRequired, code: string): Promise<MfaSetup> {
	const response = await request<{ setup: MfaSetup }>('/api/auth/mfa/setup', {
		body: { challengeId: mfa.challenge.challengeId, code },
		skipRefresh: true,
		generation: mfa.generation
	});
	return response.setup;
}
export async function finishMfaLogin(mfa: MfaRequired, code: string): Promise<AccountUser> {
	const response = await request<AuthResponse>('/api/auth/mfa/verify', {
		body: { challengeId: mfa.challenge.challengeId, code },
		skipRefresh: true,
		generation: mfa.generation
	});
	setAccessToken(response.accessToken, mfa.generation);
	return response.user;
}

export interface PublicConfig {
	registrationOpen: boolean;
	passwordResetOpen: boolean;
	datingOpen: boolean;
	classifiedsOpen: boolean;
}

export async function fetchPublicConfig(options: { strict?: boolean } = {}): Promise<PublicConfig> {
	try {
		return await request<PublicConfig>('/api/config', { skipRefresh: true });
	} catch {
		if (options.strict) throw new Error('Не удалось проверить доступность сервисов');
		return {
			registrationOpen: false,
			passwordResetOpen: false,
			datingOpen: false,
			classifiedsOpen: false
		};
	}
}

/* --- Вход по QR-коду ---------------------------------------------------- */

/** Завести код. `pollToken` держим только в памяти вкладки — в код он не идёт. */
export async function createQrLogin(): Promise<QrLoginCreated> {
	return request<QrLoginCreated>('/api/auth/qr', { body: {}, skipRefresh: true });
}

export type QrPollResult =
	{ state: Exclude<QrPollState, 'approved'> } | { state: 'approved'; user: AccountUser };

/**
 * Спросить, подтвердили ли вход. Подтверждённый код гасится этим же
 * обращением, поэтому сессия по нему выдаётся ровно один раз.
 */
export async function pollQrLogin(id: string, pollToken: string): Promise<QrPollResult> {
	const generation = getAuthorizationGeneration();
	const params = new URLSearchParams({ poll: pollToken });
	const response = await request<
		{ state: Exclude<QrPollState, 'approved'> } | ({ state: 'approved' } & AuthResponse)
	>(`/api/auth/qr/${encodeURIComponent(id)}?${params}`, { skipRefresh: true });

	if (response.state !== 'approved') return { state: response.state };

	setAccessToken(response.accessToken, generation);
	return { state: 'approved', user: response.user };
}

/** Телефон: что за устройство просит вход. */
export async function describeQrLogin(token: string): Promise<QrLoginRequestInfo> {
	const response = await request<{ request: QrLoginRequestInfo }>(
		`/api/auth/qr/scan/${encodeURIComponent(token)}`
	);

	return response.request;
}

export async function approveQrLogin(token: string, code?: string): Promise<void> {
	await request(`/api/auth/qr/scan/${encodeURIComponent(token)}/approve`, { body: { code } });
}

export async function declineQrLogin(token: string): Promise<void> {
	await request(`/api/auth/qr/scan/${encodeURIComponent(token)}/decline`, { body: {} });
}

/*
 * Возврат доступа. Задача «не бот» решается здесь по той же причине, что
 * при регистрации: без неё форма превращается в бесплатный способ рассылать
 * письма и перебирать логины.
 */
export async function requestPasswordReset(loginOrEmail: string): Promise<void> {
	const captcha = await solveCaptcha();

	await request('/api/auth/password/forgot', {
		body: { login: loginOrEmail, captchaId: captcha.id, captchaNonce: captcha.nonce },
		skipRefresh: true
	});
}

export async function resetPassword(
	loginOrEmail: string,
	code: string,
	newPassword: string
): Promise<void> {
	await request('/api/auth/password/reset', {
		body: { login: loginOrEmail, code, newPassword },
		skipRefresh: true
	});
}

export async function verifyEmail(code: string): Promise<AccountUser> {
	const response = await request<{ user: AccountUser }>('/api/auth/verify', {
		body: { code }
	});

	return response.user;
}

export async function changeVerificationEmail(
	input: VerificationEmailChange
): Promise<{ email: string }> {
	return request<{ email: string; sent: true }>('/api/auth/verify/email', {
		method: 'PATCH',
		body: input
	});
}

export async function resendCode(): Promise<void> {
	await request('/api/auth/verify/resend', { method: 'POST', body: {} });
}

export async function fetchMe(): Promise<AccountUser> {
	const response = await request<{ user: AccountUser }>('/api/auth/me');
	return response.user;
}

export async function logout(): Promise<void> {
	const generation = getAuthorizationGeneration();
	try {
		await request('/api/auth/logout', {
			method: 'POST',
			body: {},
			skipRefresh: true,
			allowSessionChange: true
		});
	} finally {
		// Токен сбрасываем в любом случае: если сервер недоступен, выйти
		// человек всё равно должен — иначе на общем компьютере это опасно.
		if (generation === getAuthorizationGeneration()) setAccessToken(null, generation);
	}
}
